import { Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy, Inject, Renderer2 } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SuiteTechnologiesComponent } from '../../../general/suite-technologies/suite-technologies.component';
import { FaqItem } from '../../../models/faq.model';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { ExploreMoreServicesComponent } from '../explore-more-services/explore-more-services.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface TimelineCard {
  id: number;
  year: string;
  title: string;
  description: string;
  image: string;
}
@Component({
  selector: 'app-qa-testing',
  imports: [CommonModule, SuiteTechnologiesComponent, FaqComponent, ExploreMoreServicesComponent],
  standalone: true,
  templateUrl: './qa-testing.component.html',
  styleUrl: './qa-testing.component.scss'
})
export class QaTestingComponent implements AfterViewInit, OnDestroy {
  @ViewChild('featuresSection') featuresSection!: ElementRef;
  @ViewChild('whyChooseSection') whyChooseSection!: ElementRef;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  isFeaturesVisible = false;
  isWhyChooseVisible = false;
  private jsonLdScript: any;
  private observer: IntersectionObserver | null = null;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
    private modalService: NgbModal
  ) { }
  goToCaseStudies(): void {

  }
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  qaFaqs: FaqItem[] = [
    {
      question: 'How has QA changed in Agile and DevOps environments?',
      answer: `QA is now integrated throughout the development cycle using Continuous Testing, 
      Test-Driven Development (TDD), and Behavior-Driven Development (BDD) to ensure quality from 
      the start.`
    },
    {
      question: 'What is Test-Driven Development (TDD)?',
      answer: `A development approach where tests are written before the code, ensuring functionality 
      meets requirements from the beginning.`
    },
    {
      question: 'What is Behavior-Driven Development (BDD)?',
      answer: `A collaborative approach using natural language scenarios (e.g., Gherkin) to 
      define expected behavior, improving communication between QA, developers, and business teams.`
    },
    {
      question: 'Why is Continuous Testing important?',
      answer: `It validates every code change quickly through automated tests integrated into CI/CD 
      pipelines, reducing risk in frequent releases.`
    },
    {
      question: 'Which tools are commonly used for automation in modern cycles?',
      answer: `Selenium, Cypress, Playwright for web; Appium for mobile; UiPath for RPA; JUnit/TestNG for unit tests.`
    },
    {
      question: 'How do QA teams handle frequent releases in CI/CD?',
      answer: `By implementing automated regression suites, smoke tests, and containerized test environments.`
    },
    {
      question: 'How can LLMs like Copilot help in test case generation?',
      answer: `LLMs can analyze requirements or user stories and suggest test cases automatically, reducing manual effort and improving coverage.`
    },
    {
      question: 'Can LLMs generate automation scripts?',
      answer: `Yes, LLMs can generate Selenium or Cypress scripts based on test case descriptions, saving time for repetitive tasks.`
    },
    {
      question: 'How do LLMs assist in QA beyond script generation?',
      answer: `They can suggest edge cases, review test coverage, and recommend improvements in test design.`
    },
    {
      question: 'Are LLM-generated test cases reliable?',
      answer: `They provide a strong starting point, but QA engineers should validate and refine them for accuracy and compliance.`
    },
    {
      question: 'What metrics are critical in Agile QA?',
      answer: `Test coverage, defect leakage, mean time to detect/fix, build stability, and automation rate.1`
    }
  ];

  @ViewChild('benefitCardsContainer', { static: false }) private benefitCardsContainer!: ElementRef;

  timelinecards: TimelineCard[] = [
    {
      id: 1,
      year: '2020',
      title: 'Automated Data Extraction Breakthrough',
      description: 'A client needed an efficient, scalable way to extract web data with minimal Azure resources and to overcome CAPTCHA barriers. Kalpita Technologies deployed UiPath, Azure Docker Containers, and 2captcha API, delivering a 99% success rate with reduced manual intervention. The solution optimized costs using Docker and achieved rapid scaling via Azure, ensuring continuous, high-efficiency data extraction for diverse formats like XLS, SQL, CSV, and XML.',
      image: '../../../../assets/services/qa/successStories/Automated-Data-Extraction-Breakthrough.png'
    },
    {
      id: 2,
      year: '2023',
      title: 'Intelligent Document Automation Excellence',
      description: 'To automate and scale the extraction of data from structured and unstructured documents, Kalpita Technologies implemented UiPath Document Understanding. The solution featured OCR digitization, ML Classifiers, and Form Extractors, with human validation for continuous improvement. Delivering over 90% accuracy in classification and extraction, the result was a dramatic reduction in manual effort and operational costs, higher processing speed, and adaptive, scalable document workflows powered by AI automation.',
      image: '../../../../assets/services/qa/successStories/Intelligent-Document-Automation-Excellence.png'
    },
    {
      id: 3,
      year: '2025',
      title: 'BDD-Powered Team Alignment',
      description: 'A leading automation framework bridged the gap between management and quality teams for Kalpita Technologies by using a BDD (Behavior-Driven Development) approach with open-source tools. The solution leveraged the Cucumber platform with Gherkin for enhanced stakeholder collaboration and maintained isolated framework and test code for maximum clarity. The result was a robust BDD framework enabling both UI and API testing, driving actionable insights and faster alignment between QA and business analysts.',
      image: '../../../../assets/services/qa/successStories/BDD-Powered-Team-Alignment.png'
    }
    // {
    //   id: 4,
    //   year: '2026',
    //   title: 'Future Vision',
    //   description: 'Leading the way in digital transformation and AI-powered solutions.',
    //   image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop'
    // },
    // {
    //   id: 5,
    //   year: '2027',
    //   title: 'Global Reach',
    //   description: 'Expanding our presence globally with cutting-edge technology solutions.',
    //   image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop'
    // }
  ];

  selectedIndex = 1; // Start with middle card selected

  // THIS IS THE FIX: All instances of 'this.cards' are changed to 'this.timelinecards'
  get visibleCards(): Array<{ card: TimelineCard, position: 'top' | 'center' | 'bottom' }> {
    const result = [];

    // Top card
    if (this.selectedIndex > 0) {
      result.push({
        card: this.timelinecards[this.selectedIndex - 1],
        position: 'top' as const
      });
    }

    // Center card (selected)
    result.push({
      card: this.timelinecards[this.selectedIndex],
      position: 'center' as const
    });

    // Bottom card
    if (this.selectedIndex < this.timelinecards.length - 1) {
      result.push({
        card: this.timelinecards[this.selectedIndex + 1],
        position: 'bottom' as const
      });
    }

    return result;
  }

  // THIS IS THE FIX: Changed 'this.cards' to 'this.timelinecards'
  get showCarouselDots(): boolean {
    return this.timelinecards.length > 3;
  }

  selectCard(position: 'top' | 'center' | 'bottom'): void {
    if (position === 'top' && this.selectedIndex > 0) {
      this.selectedIndex--;
    } else if (position === 'bottom' && this.selectedIndex < this.timelinecards.length - 1) {
      this.selectedIndex++;
    }
  }

  // THIS IS THE FIX: Changed 'this.cards' to 'this.timelinecards'
  goToCard(index: number): void {
    if (index >= 0 && index < this.timelinecards.length) {
      this.selectedIndex = index;
    }
  }

  get canScrollUp(): boolean {
    return this.selectedIndex > 0;
  }

  get canScrollDown(): boolean {
    return this.selectedIndex < this.timelinecards.length - 1;
  }

  whyChooseFeatures = [
    {
      icon: '../../../../assets/services/qa/whyChooseUs/Enterprise-GradeExpertise.png',
      title: 'Enterprise-Grade Expertise ',
      description: 'As a SOC II-certified organization, we adhere to the highest standards of security and process excellence.'
    },
    {
      icon: '../../../../assets/services/qa/whyChooseUs/Dual-MarketDeliveryModel.png',
      title: 'Dual-Market Delivery Model ',
      description: 'With headquarters in Bengaluru and offices in the USA, our QA services combine global best practices with cost-efficient offshore delivery.'
    },
    {
      icon: '../../../../assets/services/qa/whyChooseUs/IndustryExperience.png',
      title: 'Industry Experience',
      description: 'We’ve worked across Insurtech, Fintech, E-commerce, Healthcare and Hi-tech, making us a versatile QA Testing Company suited for complex domains.'
    },
    {
      icon: '../../../../assets/services/qa/whyChooseUs/QualityCultureTrust.png',
      title: 'Quality Culture & Trust',
      description: 'Recognized as a Great Place to Work since 2022, our “Kalpitians” are engaged, motivated and accountable for your QA outcomes.'
    },
    {
      icon: '../../../../assets/services/qa/whyChooseUs/End-to-EndCoverage.png',
      title: 'End-to-End Coverage ',
      description: 'From Manual QA Testing Services to Automation Testing using industry-leading tools, we provide full lifecycle support.'
    },
    {
      icon: '../../../../assets/services/qa/whyChooseUs/TailoredforUSAIndiaclients.png',
      title: 'Tailored for USA & India clients',
      description: 'Whether your team is in the USA, India, or anywhere else, our processes align to your time zone, compliance requirements, and business goals.'
    },
  ];

  developmentProcess = [
    {
      icon: '../../../../assets/services/qa/developmentProcess/DiscoveryTestStrategy.png',
      title: 'Discovery & Test Strategy',
      description: 'We engage with your team (US/India) to map application scope, risk areas (functional/non-functional) and target test environments.'
    },
    {
      icon: '../../../../assets/services/qa/developmentProcess/Test-Planning.png',
      title: 'Test-Planning',
      description: 'In collaboration, we define test cases for Functional and Non-Functional Testing, Integration flows, PBI Testing plus Automation matrix.'
    },
    {
      icon: '../../../../assets/services/qa/developmentProcess/RequirementAnalysisTestPlanning.png',
      title: 'Requirement Analysis & Test Planning',
      description: 'We understand functional & non-functional requirements, identify test scope, risks, tools, resource needs, and develop a clear & measurable QA strategy.'
    },
    {
      icon: '../../../../assets/services/qa/developmentProcess/TestCaseDesignTestDataPreparation.png',
      title: 'Test Case Design & Test Data Preparation',
      description: 'We create detailed, reusable, and traceable test cases and prepare structured test data for positive & negative scenarios.'
    },
    {
      icon: '../../../../assets/services/qa/developmentProcess/EnvironmentSetupConfiguration.png',
      title: 'Environment Setup & Configuration',
      description: 'Prepare test environments (QA, UAT, Staging) and configure integration systems, devices, and browsers.'
    },
    {
      icon: '../../../../assets/services/qa/developmentProcess/ManualAutomatedTestExecution.png',
      title: 'Manual & Automated Test Execution',
      description: 'Execute functional, regression, integration, API, and UI tests, automate repetitive and critical scenarios using tools like Selenium, UiPath, Appium, Postman, JMeter, etc.'
    },
    {
      icon: '../../../../assets/services/qa/developmentProcess/DefectTrackingReporting.png',
      title: 'Defect Tracking & Reporting',
      description: 'Log defects with clarity and priority, use tools like JIRA, Azure DevOps, or Bugzilla, and conduct root-cause analysis for prevention.'
    },
    {
      icon: '../../../../assets/services/qa/developmentProcess/PerformanceSecurityCompatibilityTesting.png',
      title: 'Performance, Security & Compatibility Testing',
      description: 'Validate speed, scalability, resilience, and security and ensure smooth experience across devices, OS, and browsers.'
    },
    {
      icon: '../../../../assets/services/qa/developmentProcess/UATSupportReleaseValidation.png',
      title: 'UAT Support & Release Validation',
      description: 'Support business users during acceptance testing and validate build stability before each release.'
    },
    {
      icon: '../../../../assets/services/qa/developmentProcess/Post-ReleaseMonitoringContinuousImprovement.png',
      title: 'Post-Release Monitoring & Continuous Improvement',
      description: 'Monitor production performance & defect leakage, implement feedback-driven enhancements, and continuously optimize test coverage & automation maturity.'
    }
  ];

  // Service Overview section data and logic
  services = [
    {
      title: 'Faster Time to Market',
      description: 'We design software that grows with your business and adapts to new technologies.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Secure & Compliant',
      description: 'Our agile development process ensures quick and efficient delivery of your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Innovation at Core',
      description: 'We build robust and secure software that complies with industry standards.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Future-Ready Architecture',
      description: 'We leverage the latest technologies to build innovative and cutting-edge software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'End to End Support',
      description: 'We provide comprehensive support and maintenance services for your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    }
  ];
  selectedServiceIndex = 3; // Default to "Future-Ready Architecture"

  // Pre-defined positions for the icons on the arc and the active position
  private arcPositions = [
    { x: -180, y: 80 },
    { x: -110, y: -20 },
    { x: 0, y: -60 },
    { x: 110, y: -20 },
    { x: 180, y: 80 }
  ];
  private activePosition = { x: 0, y: 150 };

  selectService(index: number): void {
    this.selectedServiceIndex = index;
  }

  getPointTransform(index: number): string {
    let pos;
    if (index === this.selectedServiceIndex) {
      pos = this.activePosition;
    } else {
      pos = this.arcPositions[index];
    }
    return `translate(${pos.x}px, ${pos.y}px)`;
  }

  readMore(): void {
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    if (this.observer) {
      this.observer.disconnect();
    }
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const qaTestingSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/testing-qa#webpage",
          "url": "https://www.kalpitatechnologies.com/company-services/testing-qa",
          "name": "Testing & QA Services | Manual, Automation & Performance Testing | Kalpita Technologies",
          "description": "End-to-end Testing & QA services including manual testing, automation testing, performance testing, security testing, and DevOps continuous testing.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en-US"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/company-services/testing-qa#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.kalpitatechnologies.com/company-services" },
            { "@type": "ListItem", "position": 3, "name": "Testing & QA", "item": "https://www.kalpitatechnologies.com/company-services/testing-qa" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/company-services/testing-qa#service",
          "name": "Testing & QA Services",
          "serviceType": "Quality Assurance & Software Testing",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "Comprehensive testing and quality assurance services including manual testing, automation testing, performance and load testing, and security testing."
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/company-services/testing-qa#process",
          "name": "Our Proven 10-Step Testing & QA Process",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Discovery", "text": "Understand project scope, define QA goals, and assess risks." },
            { "@type": "HowToStep", "position": 2, "name": "Requirement Analysis", "text": "Deep-dive into functional and non-functional requirements." },
            { "@type": "HowToStep", "position": 3, "name": "Test Planning", "text": "Create detailed test plans and design test cases for full coverage." },
            { "@type": "HowToStep", "position": 6, "name": "Test Execution", "text": "Execute test suites across browsers, devices, and platforms." },
            { "@type": "HowToStep", "position": 10, "name": "Monitoring", "text": "Monitor production health and track post-release defects." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/testing-qa#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What types of testing does Kalpita Technologies offer?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We offer Manual Testing, Automation Testing (Selenium, Cypress, Playwright), Performance & Load Testing, Security Testing, API Testing, and DevOps Testing."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this._renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(qaTestingSchema);
    this._renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this._renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;

        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Fast transition for smooth hover
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  setupScrollAnimation(): void {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {

          if (this.featuresSection && entry.target === this.featuresSection.nativeElement) {
            this.isFeaturesVisible = true;
            observer.unobserve(entry.target);
          }

          if (this.whyChooseSection && entry.target === this.whyChooseSection.nativeElement) {
            this.isWhyChooseVisible = true;
            observer.unobserve(entry.target);
          }
        }
      });
    }, {
      threshold: 0.2 // Trigger when 20% visible
    });

    if (this.featuresSection) {
      observer.observe(this.featuresSection.nativeElement);
    }

    if (this.whyChooseSection) {
      observer.observe(this.whyChooseSection.nativeElement);
    }

    this.observer = observer;
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    this.setupTiltEffect();
    this.setupScrollAnimation();
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Optional: Disable on touch devices for better scrolling
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  getAFreeQuote(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  bookAFreeCall(): void {
    this.getAFreeQuote();
  }
}
